<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ mealPlan.name[hotelSettings.details.primaryLanguage] }}
      </portal>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="mealPlan.name[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="mealPlan.deleted"
                      :label="$t('admin.pageMealPlan.title')"
                      validate="required"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="mealPlan.description[hotelSettings.details.primaryLanguage]"
                         :lazy="true"
                         :disabled="mealPlan.deleted"
                         :label="$t('admin.pageMealPlan.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="mealPlan.name[lang]"
                      :lazy="true"
                      :disabled="mealPlan.deleted"
                      :label="$t('admin.pageMealPlan.title')"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="mealPlan.description[lang]"
                         :lazy="true"
                         :disabled="mealPlan.deleted"
                         :label="$t('admin.pageMealPlan.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pageMealPlan.card.general')" cols="12">
        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="mealPlan.mealPlanType"
                           :disabled="mealPlan.deleted"
                           :label="$t('admin.pageMealPlan.type')"
                           name="type"
                           enum-name="MealPlanType"
                           cols="12 6@tablet 3@desktop" />
          <hm-multiselect id="meals"
                          v-model="mealPlan.meals"
                          :options="mealPlanOptions"
                          :disabled="mealPlan.deleted"
                          :label="$t('admin.pageMealPlan.meals')"
                          name="meals"
                          label-expression="${option.name}"
                          cols="12 6@tablet 3@desktop" />
          <hm-input-toggle v-model="mealPlan.visibleForGuest"
                           :disabled="mealPlan.deleted"
                           :label="$t('admin.pageMealPlan.isVisibleForGuest')"
                           :help="$t('admin.pageMealPlan.isVisibleForGuest_help')"
                           cols="12 6@tablet 3@desktop" />
          <hm-input-money v-model="mealPlan.amount"
                          :disabled="mealPlan.deleted"
                          :currency="`${mealPlan.percentage ? '%' : hotelSettings.details.currency}`"
                          :digits="2"
                          :lazy="true"
                          :currency-clickable="true"
                          :label="$t('admin.pageMealPlan.amount')"
                          name="amount"
                          cols="12 6@tablet 3@desktop start-1"
                          @currency-click="mealPlan.percentage = !mealPlan.percentage">
            <template slot="icon-inline-right">
              <input-icon-button v-if="mealPlan.rateMode === 'PerRoom'"
                                 :disabled="true"
                                 :border="true"
                                 :title="$t(`RateMode.${mealPlan.rateMode}`)"
                                 icon="per-room"
                                 @click="mealPlan.rateMode = 'PerPerson'" />
              <input-icon-button v-else-if="mealPlan.rateMode === 'PerPerson'"
                                 :disabled="true"
                                 :border="true"
                                 :title="$t(`RateMode.${mealPlan.rateMode}`)"
                                 icon="per-person"
                                 @click="mealPlan.rateMode = 'PerRoom'" />
              <input-icon-button v-if="mealPlan.rateFrequency === 'PerStay'"
                                 :disabled="true"
                                 :border="true"
                                 :title="$t(`RateFrequency.${mealPlan.rateFrequency}`)"
                                 icon="per-stay"
                                 @click="mealPlan.rateFrequency = 'PerNight'" />
              <input-icon-button v-else-if="mealPlan.rateFrequency === 'PerNight'"
                                 :disabled="true"
                                 :border="true"
                                 :title="$t(`RateFrequency.${mealPlan.rateFrequency}`)"
                                 icon="per-night"
                                 @click="mealPlan.rateFrequency = 'PerStay'" />
            </template>
          </hm-input-money>
          <hm-multiselect id="applicableTaxes"
                          v-model="mealPlan.applicableTaxes"
                          :options="hotelSettings.taxes.all(mealPlan.applicableTaxes).filter(it => !it.deleted).filter(it => mealPlan.applicableTaxes.includes(it.id) || it.taxType === 'VAT' || (it.taxType === 'Other' && it.percentage))"
                          :disabled="mealPlan.deleted"
                          :label="$t('admin.pageMealPlan.applicableTaxes')"
                          cols="12 6@tablet 3@desktop"
                          name="applicableTaxes"
                          label-expression="${option.name[$shortLocale]}" />
          <hm-multiselect v-if="mealPlan.rateMode === 'PerPerson'"
                          id="ageCategories"
                          v-model="mealPlan.ageCategories"
                          :options="hotelSettings.ageCategories.all(mealPlan.ageCategories).filter(it => !it.deleted)"
                          :disabled="mealPlan.deleted"
                          :label="$t('admin.pageMealPlan.ageCategories')"
                          cols="12 6@tablet 3@desktop"
                          name="ageCategories"
                          label-expression="${option.name[$shortLocale]}" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="mealPlan.fiscalizationChargeType"
                           :disabled="mealPlan.deleted || !hasRole('ROLE_SYSADMIN')"
                           :label="$t('admin.pageMealPlan.fiscalizationChargeType')"
                           name="type"
                           enum-name="FiscalizationChargeType"
                           cols="12 6@tablet 3@desktop" />

          <hm-input v-if="hasAnyLicense(['LedgerReport', 'DatevReport'])"
                    id="ledgerCode"
                    v-model="mealPlan.ledgerCode"
                    :disabled="mealPlan.deleted"
                    :label="$t('admin.pageMealPlan.ledgerCode')"
                    cols="12 6@tablet 3@desktop"
                    name="ledgerCode" />

          <hm-input v-if="hasLicense('FiskaltrustAgencyBusiness')"
                    id="fiscalizationAgencyId"
                    v-model="mealPlan.fiscalizationAgencyId"
                    :disabled="mealPlan.deleted || !hasRole('ROLE_SYSADMIN')"
                    :label="$t('admin.pageMealPlan.fiscalizationAgencyId')"
                    cols="12 6@tablet 3@desktop "
                    name="fiscalizationAgencyId" />
        </hm-card-inner>
      </card>

      <card v-if="!canDelete" :title="$t('admin.pageMealPlan.card.usage')" cols="12" card-type="collapsible">
        <hm-card-inner v-if="!isEmpty(usedRatePlans)" :full-size="true">
          <hm-col cols="12">
            <template v-for="it in usedRatePlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRatePlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!mealPlan.deleted && canDelete"
                            :label="$t('admin.pageMealPlan.actions.deleteMealPlan')"
                            icon="delete"
                            color="error"
                            @click="deleteMealPlan(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';

export default {
  name: 'PageMealPlan',
  components: { HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'mealPlan',
      variable: 'mealPlan',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.mealPlan.deleted === true) return;

        const toSave = cloneDeep(this.mealPlan);

        this.axios.put(this.serviceUrls.mealPlan.get(this, this.mealPlanId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('mealPlan.list');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      mealPlan: {
        rateMode: null,
        rateFrequency: null,
      },
      mealPlanId: this.$route.params.mealPlanId,
      dataLoaded: false,
    };
  },

  computed: {
    usedRatePlans: function () {
      return this.hotelSettings.ratePlans.filter(it => !it.deleted && !isNil(it.includedMealPlan) && it.includedMealPlan.id === this.mealPlan.id);
    },
    canDelete: function () {
      return this.usedRatePlans.length === 0;
    },
    mealPlanOptions: function () {
      return this.$store.getters['enums/enums'].Meal.map(it => ({
        id: it,
        name: this.$t(`Meal.${it}`),
      }));
    },
  },

  watch: {
    mealPlan: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('mealPlan.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.mealPlan.get(this, this.mealPlanId))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.mealPlan = response.data.data;
            this.dataLoaded = true;
          }
        });
    },
    deleteMealPlan(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.mealPlan.get(this, this.mealPlanId))
            .then((response) => {
              this.dataLoaded = false;
              this.mealPlan = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('mealPlan.list');
              this.$router.push({
                name: 'mealPlans',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageMealPlan.confirmDeleteMealPlan'),
            (doNotShowAgain) => {
              this.deleteMealPlan(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageMealPlan.mealPlanInUse'));
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageMealPlan.vue